import { Controller } from "@hotwired/stimulus"

/**
 * LinearIssueSelectorController - Multi-select component for Linear issues
 *
 * Used in Ralph session forms to select Linear issues for AI-assisted plan generation.
 *
 * Usage:
 *   <div data-controller="linear-issue-selector"
 *        data-linear-issue-selector-url-value="/api/v2/app_projects/slug/ralph_sessions/linear_issues">
 *     <input type="text" data-linear-issue-selector-target="search" placeholder="Search issues...">
 *     <div data-linear-issue-selector-target="issueList"></div>
 *     <div data-linear-issue-selector-target="selectedList"></div>
 *     <input type="hidden" data-linear-issue-selector-target="selectedIds" name="ralph_session[linear_issue_ids][]">
 *   </div>
 */
export default class extends Controller {
  static targets = [
    "search",
    "issueList",
    "selectedIds",
    "selectedCount",
    "loading",
    "emptyState",
    "teamFilter",
    "statusFilter",
    "labelFilter"
  ]

  static values = {
    url: String,
    selected: { type: Array, default: [] }
  }

  connect() {
    console.log("Linear issue selector controller connected")
    this.selectedIssues = new Map() // Map of linear_id -> issue data
    this.allIssues = []
    this.loadIssues()
  }

  async loadIssues() {
    if (!this.urlValue) {
      console.error("No URL provided for Linear issues")
      this.showEmptyState("No Linear integration configured")
      return
    }

    this.showLoading(true)

    try {
      const url = new URL(this.urlValue, window.location.origin)

      // Add filters if present
      if (this.hasTeamFilterTarget && this.teamFilterTarget.value) {
        url.searchParams.set('team_id', this.teamFilterTarget.value)
      }
      if (this.hasStatusFilterTarget && this.statusFilterTarget.value) {
        url.searchParams.set('status', this.statusFilterTarget.value)
      }
      if (this.hasLabelFilterTarget && this.labelFilterTarget.value) {
        url.searchParams.set('label', this.labelFilterTarget.value)
      }
      if (this.hasSearchTarget && this.searchTarget.value) {
        url.searchParams.set('search', this.searchTarget.value)
      }

      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        },
        credentials: 'same-origin'  // Include session cookies for authentication
      })

      const data = await response.json()

      if (data.success && data.data) {
        this.allIssues = data.data.issues || []
        this.renderIssues(this.allIssues)
        this.populateFilters(data.data.teams, data.data.projects, data.data.labels)

        if (this.allIssues.length === 0) {
          this.showEmptyState(data.data.message || "No issues found")
        }
      } else {
        this.showEmptyState(data.error || "Failed to load issues")
      }
    } catch (error) {
      console.error("Error loading Linear issues:", error)
      this.showEmptyState("Error loading issues: " + error.message)
    } finally {
      this.showLoading(false)
    }
  }

  renderIssues(issues) {
    if (!this.hasIssueListTarget) return

    if (issues.length === 0) {
      this.issueListTarget.innerHTML = ''
      return
    }

    const html = issues.map(issue => this.renderIssueRow(issue)).join('')
    this.issueListTarget.innerHTML = html
  }

  renderIssueRow(issue) {
    const isSelected = this.selectedIssues.has(issue.linear_id)
    const priorityBadge = this.getPriorityBadge(issue.priority)
    const statusBadge = this.getStatusBadge(issue.status)
    const labels = (issue.labels || []).slice(0, 3).map(l =>
      `<span class="badge badge-outline badge-xs">${this.escapeHtml(l)}</span>`
    ).join(' ')

    return `
      <div class="flex items-start gap-3 p-3 hover:bg-base-200 rounded-lg cursor-pointer transition-colors border border-transparent hover:border-base-300 ${isSelected ? 'bg-primary/10 border-primary/30' : ''}"
           data-action="click->linear-issue-selector#toggleIssue"
           data-linear-id="${issue.linear_id}"
           data-issue='${JSON.stringify(issue).replace(/'/g, "&#39;")}'>
        <input type="checkbox"
               class="checkbox checkbox-sm checkbox-primary mt-1"
               ${isSelected ? 'checked' : ''}
               data-linear-id="${issue.linear_id}">
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="font-mono text-sm text-primary font-medium">${this.escapeHtml(issue.identifier)}</span>
            ${priorityBadge}
            ${statusBadge}
          </div>
          <p class="text-sm font-medium mt-1 line-clamp-2">${this.escapeHtml(issue.title)}</p>
          ${issue.description ? `<p class="text-xs text-base-content/60 mt-1 line-clamp-2">${this.escapeHtml(issue.description)}</p>` : ''}
          <div class="flex items-center gap-2 mt-2 flex-wrap">
            ${labels}
            ${issue.team_name ? `<span class="text-xs text-base-content/50">${this.escapeHtml(issue.team_name)}</span>` : ''}
          </div>
        </div>
      </div>
    `
  }

  toggleIssue(event) {
    // Don't toggle if clicking directly on checkbox (it handles itself)
    const checkbox = event.target.closest('[data-linear-id]')?.querySelector('input[type="checkbox"]')
    const linearId = event.currentTarget.dataset.linearId
    const issueData = JSON.parse(event.currentTarget.dataset.issue)

    if (this.selectedIssues.has(linearId)) {
      this.selectedIssues.delete(linearId)
      if (checkbox) checkbox.checked = false
      event.currentTarget.classList.remove('bg-primary/10', 'border-primary/30')
    } else {
      this.selectedIssues.set(linearId, issueData)
      if (checkbox) checkbox.checked = true
      event.currentTarget.classList.add('bg-primary/10', 'border-primary/30')
    }

    this.updateSelectedIds()
    this.updateSelectedCount()
  }

  selectAll() {
    // Select all visible issues in the current filtered list
    // Use [data-issue] to only select the row divs, not the checkboxes inside them
    const issueRows = this.issueListTarget?.querySelectorAll('[data-linear-id][data-issue]') || []
    issueRows.forEach(row => {
      const linearId = row.dataset.linearId
      const issueData = JSON.parse(row.dataset.issue)
      const checkbox = row.querySelector('input[type="checkbox"]')

      if (!this.selectedIssues.has(linearId)) {
        this.selectedIssues.set(linearId, issueData)
        if (checkbox) checkbox.checked = true
        row.classList.add('bg-primary/10', 'border-primary/30')
      }
    })

    this.updateSelectedIds()
    this.updateSelectedCount()
  }

  deselectAll() {
    // Deselect all issues
    this.selectedIssues.clear()

    // Update UI for all visible rows
    const issueRows = this.issueListTarget?.querySelectorAll('[data-linear-id][data-issue]') || []
    issueRows.forEach(row => {
      const checkbox = row.querySelector('input[type="checkbox"]')
      if (checkbox) checkbox.checked = false
      row.classList.remove('bg-primary/10', 'border-primary/30')
    })

    this.updateSelectedIds()
    this.updateSelectedCount()
  }

  updateSelectedIds() {
    if (!this.hasSelectedIdsTarget) return

    // Clear existing hidden inputs
    const container = this.selectedIdsTarget.parentElement
    container.querySelectorAll('input[name="ralph_session[linear_issue_ids][]"]').forEach(input => {
      if (input !== this.selectedIdsTarget) input.remove()
    })

    // Create hidden inputs for each selected issue
    const selectedIds = Array.from(this.selectedIssues.keys())

    if (selectedIds.length === 0) {
      // Keep one empty input so the param is submitted
      this.selectedIdsTarget.value = ''
    } else {
      this.selectedIdsTarget.value = selectedIds[0]

      // Add additional hidden inputs for remaining IDs
      selectedIds.slice(1).forEach(id => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = 'ralph_session[linear_issue_ids][]'
        input.value = id
        container.appendChild(input)
      })
    }
  }

  updateSelectedCount() {
    if (!this.hasSelectedCountTarget) return

    const count = this.selectedIssues.size
    this.selectedCountTarget.textContent = count
    this.selectedCountTarget.classList.toggle('badge-primary', count > 0)
  }

  populateFilters(teams, projects, labels) {
    if (this.hasTeamFilterTarget && teams && teams.length > 0) {
      const options = teams.map(t =>
        `<option value="${t.id}">${this.escapeHtml(t.name)}</option>`
      ).join('')
      this.teamFilterTarget.innerHTML = `<option value="">All Teams</option>${options}`
    }
    if (this.hasLabelFilterTarget && labels && labels.length > 0) {
      const options = labels.map(l =>
        `<option value="${this.escapeHtml(l)}">${this.escapeHtml(l)}</option>`
      ).join('')
      this.labelFilterTarget.innerHTML = `<option value="">All Labels</option>${options}`
    }
  }

  // Event handlers for filters
  filterByTeam() {
    this.loadIssues()
  }

  filterByStatus() {
    this.loadIssues()
  }

  filterByLabel() {
    this.loadIssues()
  }

  search() {
    // Debounce search
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => {
      this.loadIssues()
    }, 300)
  }

  clearSearch() {
    if (this.hasSearchTarget) {
      this.searchTarget.value = ''
    }
    this.loadIssues()
  }

  // Helper methods
  getPriorityBadge(priority) {
    const priorities = {
      0: { label: 'No Priority', class: 'badge-ghost' },
      1: { label: 'Urgent', class: 'badge-error' },
      2: { label: 'High', class: 'badge-warning' },
      3: { label: 'Medium', class: 'badge-info' },
      4: { label: 'Low', class: 'badge-ghost' }
    }
    const p = priorities[priority] || priorities[0]
    return `<span class="badge badge-xs ${p.class}">${p.label}</span>`
  }

  getStatusBadge(status) {
    if (!status) return ''
    const statusClass = status.toLowerCase().includes('done') ? 'badge-success' :
                       status.toLowerCase().includes('progress') ? 'badge-info' :
                       status.toLowerCase().includes('cancel') ? 'badge-error' : 'badge-ghost'
    return `<span class="badge badge-xs ${statusClass}">${this.escapeHtml(status)}</span>`
  }

  showLoading(show) {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.toggle('hidden', !show)
    }
    if (this.hasIssueListTarget) {
      this.issueListTarget.classList.toggle('hidden', show)
    }
  }

  showEmptyState(message) {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.textContent = message
      this.emptyStateTarget.classList.remove('hidden')
    }
    if (this.hasIssueListTarget) {
      this.issueListTarget.innerHTML = ''
    }
  }

  escapeHtml(text) {
    if (!text) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }
}
